import { eventQBus } from "../types/EventQBus";
import { forEachElement, mapElements } from "../util/Utils";
import { OverflowBoxItem } from "../overflowBox/OverflowBox";
import tracker from "../tracking/Tracker";
import { isActive } from "../toggle/Toggle";
import { events } from "../events";

const SORT_PARAM = "sortiertnach";
const SELECTOR_SORTING_INPUT = `[name='${SORT_PARAM}']`;

export abstract class SortingInput {
  /*               */
  protected constructor() {}

  static #create(elem: HTMLInputElement | HTMLSelectElement): SortingInput {
    return elem instanceof HTMLSelectElement ? SortingSelectInput.create(elem) : SortingGenericInput.create(elem);
  }

  /*                  */

  static closest(elem: HTMLElement | EventTarget | null) {
    if (elem instanceof HTMLElement) {
      const input = elem.closest<HTMLInputElement | HTMLSelectElement>(SELECTOR_SORTING_INPUT);
      return input ? SortingInput.#create(input) : undefined;
    }
  }

  static forEach(callback: (input: SortingInput, index: number) => void, rootElement?: ParentNode) {
    forEachElement<HTMLInputElement | HTMLSelectElement>(
      SELECTOR_SORTING_INPUT,
      (elem, index) => callback(SortingInput.#create(elem), index),
      rootElement,
    );
  }

  static all(root?: ParentNode | null) {
    return mapElements(SELECTOR_SORTING_INPUT, SortingInput.#create, root);
  }

  /*               */

  static register() {
    eventQBus.on("heureka.filterSection.loaded", SortingInput.initAll);
    /*                                                                             */
    if (isActive("HEUREKA_2407_MIGRATE_TO_REPTILE_NEXUS")) {
      events.onDresonRuleResolved((state) => {
        SortingInput.resync(state?.page?.sortOrder);
      });
    } else {
      eventQBus.on("ftfind.dresonRule.resolved", (data) => {
        SortingInput.resync(data?.page?.sortiertnach);
      });
    }
  }

  static resync(value?: string, rootElement?: ParentNode) {
    const inputs = SortingInput.all(rootElement);
    const target = value || inputs.find((input) => input.defaultValue)?.defaultValue;
    if (target) {
      inputs.forEach((input) => (input.selected = target));
    }
  }

  static resyncToCurrentUrl(rootElement?: ParentNode) {
    const value = new URLSearchParams(location.search).get(SORT_PARAM) ?? undefined;
    SortingInput.resync(value, rootElement);
  }

  static initAll(event?: { page?: { sortiertnach?: string } }, rootElement?: ParentNode) {
    const { page } = event || {};
    const { sortiertnach } = page || {};

    SortingInput.forEach((input) => input.init(sortiertnach), rootElement);
  }

  protected init(value?: string) {
    if (value) {
      this.selected = value;
    }
  }

  /*                       */

  abstract set selected(value: string);

  get defaultValue(): string | undefined {
    return undefined;
  }
}

class SortingGenericInput extends SortingInput {
  private constructor(readonly input: HTMLInputElement) {
    super();
  }

  static create(elem: HTMLInputElement) {
    return new SortingGenericInput(elem);
  }

  /*                       */

  set selected(value: string) {
    switch (this.input.type) {
      case "radio":
      case "checkbox":
        this.input.checked = this.input.value === value;
        OverflowBoxItem.toggleFirstItem(this.input, this.input.checked);
        break;
      case "hidden":
      case "text":
        this.input.value = value;
        break;
      default:
        /*                                                               */
        if ("checked" in this.input) {
          this.input.checked = this.input.value === value;
          OverflowBoxItem.toggleFirstItem(this.input, this.input.checked);
        }
    }
  }

  get defaultValue(): string | undefined {
    switch (this.input.type) {
      case "radio":
      case "checkbox":
        return this.input.defaultChecked ? this.input.value : undefined;
      case "hidden":
      case "text":
        return this.input.defaultValue || undefined;
      default:
        /*                                                                                             */
        return this.input.hasAttribute("checked") ? this.input.value : undefined;
    }
  }
}

class SortingSelectInput extends SortingInput {
  private constructor(readonly select: HTMLSelectElement) {
    super();
  }

  static create(elem: HTMLSelectElement) {
    return new SortingSelectInput(elem);
  }

  /*               */

  protected init(value: string) {
    super.init(value);
    this.registerOnClick();
  }

  private registerOnClick() {
    const initialSelectedOption = this.select.querySelector<HTMLOptionElement>("option[selected]")?.value;
    this.select.addEventListener(
      "click",
      (event) => {
        if (event.target instanceof HTMLSelectElement && event.target.value === initialSelectedOption) {
          SortingSelectInput.sortingOpened();
        }
      },
      {
        capture: true,
      },
    );
  }

  /*                       */

  set selected(value: string) {
    forEachElement<HTMLOptionElement>(
      "option",
      (option) => {
        const isSelected = option.value === value;
        option.defaultSelected = isSelected;
        option.selected = isSelected;
      },
      this.select,
    );
    /*                                              */
    this.select.closest("hc-select-chip")?.setAttribute("selected-value", value);
  }

  get defaultValue(): string | undefined {
    return this.select.querySelector<HTMLOptionElement>("option[selected]")?.value;
  }

  /*                 */

  private static sortingOpened() {
    tracker.submitEvent({ san_Foldout: "sorting_open" });
  }
}
